import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { ReactComponent as Logo } from "../../assets/icons/animation-logo.svg";
import { ActionButtons } from "./ActionButtons";
import { usePraxisCall } from "../praxis-play/context/praxiscall-context";
import { AudioPlayer } from "./components/AudioPlayer";
import ClockTimer from "./components/ClockTimer";
import { PraxisLoading } from "./PraxisLoading";
import { useApp } from "../../services/app-context";
import { useUser } from "../../services/user-context";
import Lottie from "lottie-react";
import * as animationData from "../../assets/loading-dots.json";
import { isCallDetailsEnabled, isCallFocusEnabled } from "../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../integrations/posthog/usePostHogFeatureFlag";
import { hasTenantCallFocusTopics } from "../praxis-play/CustomizerModal";
import { CallDetailsPopover, type CallDetailRow } from "./components/CallDetailsPopover";
import { buildCallDetailRows } from "./callDetailRows";
import { InternetConnectionPopover } from "./components/InternetConnectionPopover";
import { SignedUrlImage } from "../../components/SignedUrlImage";
import { ShowToast } from "../../services/toast";
import DocumentViewer from "./components/DocumentViewer";
import { useTutorial } from "../../contexts/TutorialContext";
import { TutorialTooltip } from "../../components/TutorialTooltip";
import { EndSessionModal } from "./components/EndSessionModal";
import { clearCallStateOnDisconnectError } from "../../contexts/disconnect-helpers";
import type { NoiseReductionType } from "./noiseReduction";
import { createCallRecording } from "./recording";
import { createPraxisCallSession } from "./session";
import { twMerge } from "tailwind-merge";

const audioPlayer = new AudioPlayer();

export function PraxisCall({ saving = false }: { saving?: boolean }) {
  const { tenantProfile, styles } = useApp();
  const recordingCountRef = useRef(1);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const { userInfo, credentials } = useUser() as any;
  const {
    callDetails,
    onDisconnect,
    setLoading,
    setCallDetails,
    setCallStatus,
    transcriptRef,
    sentenceCacheRef,
    setDataSavingProgress,
  } = usePraxisCall();

  // Refs to avoid stale closures in MediaRecorder onstop (set at recording start)
  const onDisconnectRef = useRef(onDisconnect);
  const callDetailsRef = useRef(callDetails);
  onDisconnectRef.current = onDisconnect;
  callDetailsRef.current = callDetails;
  const [isConnected, setIsConnected] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const isCallDetailsEnabledFlag = usePostHogFeatureFlag(isCallDetailsEnabled);

  // Tutorial support - only show steps 4-6 if session is active
  const { nextStep, skip, getCurrentStepConfig, isVisible } = useTutorial();
  const currentStep = getCurrentStepConfig();
  // Only show tutorial if step requires active session and we're connected
  const shouldShowTutorial = currentStep?.requiresActiveSession 
    ? isConnected 
    : isVisible;
  const incomingAudioChunks = useRef<any[]>([]);
  const outgoingAudioChunks = useRef<any[]>([]);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isCountdownComplete, setIsCountdownComplete] = useState(false);
  const isCountdownCompleteRef = useRef(false);
  const outgoingMediaRecorderRef = useRef<any>(null);
  const incomingMediaRecorderRef = useRef<any>(null);
  const timeLeftRef = useRef<number>(0);

  const mediaRecorderRef = useRef<any>(null);
  const audioMediaRecorderRef = useRef<any>(null);

  const [voiceIntensity, setVoiceIntensity] = useState(0);

  const audioContextRef = useRef<any>(null);
  const volumeMonitorIntervalRef = useRef<any>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const webcamStreamRef = useRef<MediaStream | null>(null);
  const webcamVideoRef = useRef<HTMLVideoElement | null>(null);

  const currentStream = useRef<any | null>(null);
  const currentConnection = useRef<any | null>(null);
  const dataChannel = useRef<any>(null);
  const lastCallIdRef = useRef<string | null>(null);
  // System instructions captured from session.created, re-injected with each response.create.
  const baseInstructionsRef = useRef<string>("");
  // Incremented on every user speech turn and on session reset (never reset to 0).
  // fetchContextAndTriggerResponse captures the value before its network await; if it
  // has changed by the time the await resolves, the turn was interrupted or the session
  // ended and the stale result must be discarded.
  const contextRequestGenerationRef = useRef(0);
  const isWaitingForModelResponseRef = useRef(false);
  const isUserCurrentlySpeakingRef = useRef(false);
  // Buffers the assistant transcript from response.output_audio_transcript.done until
  // response.done confirms status "completed". Discarded on interruption/failure
  // so partial transcripts from cancelled responses never enter transcriptRef.
  const pendingAssistantTranscriptRef = useRef<string>("");
  // Tracks the last fully-completed assistant message. Only set on completed
  // responses. Cleared on interruption so the backend never receives stale
  // hcpSentence from a cancelled turn.
  const lastCompletedAssistantMessageRef = useRef<string>("");
  const pendingEndReasonRef = useRef<string | null>(null);
  const videoEndReasonResolveRef = useRef<(() => void) | null>(null);
  const videoEndReasonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dcOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref, not a closure var — createPraxisCallSession() re-runs every render.
  const handlerCleanupRef = useRef<(() => void) | null>(null);
  const noiseReductionTypeRef = useRef<NoiseReductionType>('near_field');
  const deviceMonitorCleanupRef = useRef<(() => void) | null>(null);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);

  const clearCallStateOnError = useCallback(() => {
    clearCallStateOnDisconnectError(
      { setLoading, setCallDetails, setCallStatus, setDataSavingProgress },
      transcriptRef
    );
  }, [setLoading, setCallDetails, setCallStatus, setDataSavingProgress, transcriptRef]);
  const clearCallStateOnErrorRef = useRef(clearCallStateOnError);
  clearCallStateOnErrorRef.current = clearCallStateOnError;

  // Reset per-session state when a new call id is set.
  useEffect(() => {
    const callId = (callDetails as any)?.id as string | undefined;
    if (!callId) return;

    if (lastCallIdRef.current !== callId) {
      lastCallIdRef.current = callId;
      baseInstructionsRef.current = "";
      contextRequestGenerationRef.current++;
      pendingAssistantTranscriptRef.current = "";
      lastCompletedAssistantMessageRef.current = "";
    }
  }, [callDetails]);

  const { startRecording, stopRecording, getAudioRecordings, restartAudioRecording } = createCallRecording({
    mediaRecorderRef,
    audioMediaRecorderRef,
    outgoingMediaRecorderRef,
    incomingMediaRecorderRef,
    incomingAudioChunks,
    outgoingAudioChunks,
    recordingCountRef,
    pendingEndReasonRef,
    videoEndReasonResolveRef,
    videoEndReasonTimeoutRef,
    screenStreamRef,
    webcamStreamRef,
    webcamVideoRef,
    callDetailsRef,
    onDisconnectRef,
    clearCallStateOnErrorRef,
    clearCallStateOnError,
    setLoading,
    setDataSavingProgress,
    tenantProfile,
    credentials,
    userInfo,
    callDetails,
    onDisconnect,
  });

  const { startSession, stopSession } = createPraxisCallSession({
    currentStream,
    currentConnection,
    dataChannel,
    dcOpenTimeoutRef,
    handlerCleanupRef,
    noiseReductionTypeRef,
    deviceMonitorCleanupRef,
    audioContextRef,
    callDetailsRef,
    incomingMediaRecorderRef,
    incomingAudioChunks,
    outgoingMediaRecorderRef,
    outgoingAudioChunks,
    contextRequestGenerationRef,
    isWaitingForModelResponseRef,
    isUserCurrentlySpeakingRef,
    pendingAssistantTranscriptRef,
    lastCompletedAssistantMessageRef,
    baseInstructionsRef,
    transcriptRef,
    sentenceCacheRef,
    timeLeftRef,
    isCountdownCompleteRef,
    setIsUploading,
    setIsConnected,
    setVoiceIntensity,
    setLoading,
    setDataSavingProgress,
    callDetails,
    onDisconnect,
    clearCallStateOnError,
    recording: { startRecording, stopRecording, getAudioRecordings },
  });

  const stopMonitoringVoiceIntensity = useCallback(() => {
    if (volumeMonitorIntervalRef.current) {
      clearInterval(volumeMonitorIntervalRef.current);
      setVoiceIntensity(0);
      volumeMonitorIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    document.addEventListener("playbackFinished", stopMonitoringVoiceIntensity);
    return () => {
      document.removeEventListener(
        "playbackFinished",
        stopMonitoringVoiceIntensity
      );
    };
  }, [stopMonitoringVoiceIntensity]);

  useEffect(() => {
    if (!audioPlayer.volumeMonitorInterval && callDetails?.isVideoRecording) {
      restartAudioRecording();
    }
  }, [audioPlayer.volumeMonitorInterval, callDetails?.isVideoRecording]);

  useEffect(() => {
    if (callDetails?.clientSecret && !saving) {
      startSession({ clientSecret: callDetails?.clientSecret });
    }
  }, [callDetails?.clientSecret, saving]);

  useEffect(() => {
    if (isConnected) {
      // Mute mic for the duration of the 3-2-1 countdown so OpenAI receives no audio
      // and no server-side conversation items are created before the session is live.
      // onCountdownComplete (which re-enables the tracks) is the only path that sets
      // showCountdown=false, so PraxisLoading stays mounted until the countdown
      // finishes — there is no way for the mic to remain muted into a live session.
      currentStream.current?.getTracks().forEach((t: MediaStreamTrack) => { t.enabled = false; });
      setShowCountdown(true);
    }
  }, [isConnected]);

  const isPracticeMode = callDetails?.isPracticeMode;

  const callFocusFlagOn = usePostHogFeatureFlag(isCallFocusEnabled);
  const sessionGroup = userInfo?.group;
  const callFocusAvailable = useMemo(
    () => callFocusFlagOn && hasTenantCallFocusTopics(tenantProfile, sessionGroup),
    [callFocusFlagOn, tenantProfile, sessionGroup],
  );

  const callDetailRows = useMemo(
    (): CallDetailRow[] =>
      buildCallDetailRows({ callDetails, tenantProfile, sessionGroup, callFocusAvailable }),
    [callDetails, tenantProfile, sessionGroup, callFocusAvailable],
  );

  return (
    <div className="fixed z-[9] top-0 bottom-0 left-0 right-0">
      {/* Left column: all call UI. Shrinks to 50% when sales aid panel is open; sales aid panel is a separate fixed right half. */}
      <div
        className={twMerge(
          "absolute left-0 top-0 bottom-0 transition-all duration-300",
          selectedDocument ? "w-1/2" : "w-full",
          isPracticeMode ? "praxis-call--practice" : "praxis-call--game",
        )}
      >
        <div className="text-text-primary absolute top-0 left-0 w-full p-10 flex flex-col gap-1">
          <span className="text-[21px] font-semibold">{isPracticeMode ? "Practice Mode" : "Game Mode"}</span>
          <span className="text-[13px] font-medium">{isPracticeMode ? "This session will not affect your Avg. Praxis Score. You will still be able to see feedback" : "This session will affect your Avg. Praxis Score. You will be able to see feedback after the session is over."}</span>
        </div>
        <DocumentViewer
          onSelectDocument={setSelectedDocument}
          selectedDocument={selectedDocument}
          sessionId={(callDetails as any)?.id as string | undefined}
        />
        <button
          type="button"
          onClick={() => setShowEndSessionModal(true)}
          className="absolute top-10 right-[30px] z-10 w-10 h-10 rounded-full bg-background border border-gray2 text-text-primary shadow-sm hover:opacity-90 transition-opacity flex items-center justify-center"
          aria-label="End call"
        >
          <CloseIcon className="w-5 h-5 [&_path]:stroke-current" />
        </button>
        <EndSessionModal
          isOpen={showEndSessionModal}
          onClose={() => setShowEndSessionModal(false)}
          onEndSession={async (endReason) => {
            setShowEndSessionModal(false);
            await stopSession(endReason);
          }}
          onError={() => {
            /* Pre-API errors only (getAudioRecordings, streams). API errors are handled in onDisconnect. */
            clearCallStateOnError();
            ShowToast({ message: "Failed to end session. Please try again.", type: "error" });
          }}
        />
        <ActionButtons
          onDisconnect={() => stopSession()}
          className="w-full"
          audioStream={currentStream.current}
          sessionStarted={isCountdownComplete}
          muteMicPopupDurationMs={tenantProfile?.mute_mic_popup_duration_ms}
        />
        {isConnected && isCallDetailsEnabledFlag && (
          <CallDetailsPopover
            rows={callDetailRows}
            visible={callDetailRows.length > 0}
            className={twMerge("absolute left-10 top-32", !showCountdown ? "z-20" : "")}
          />
        )}
        {isConnected && isCallDetailsEnabledFlag && (
          <InternetConnectionPopover
            className={twMerge("absolute left-10 bottom-10", !showCountdown ? "z-20" : "")}
          />
        )}
        <div
          className={twMerge(
            "w-full praxis-call-background",
            isPracticeMode
              ? "praxis-call-background--practice"
              : "praxis-call-background--game",
          )}
        >
          <div className="w-full ai-interaction-container">
            <div className="flex flex-col items-center gap-6">
              <div
                className={`relative animation-wrapper ${isUploading ? "loading" : "ready"
                  }`}
              >
                <div className="ai-dots">
                  <Lottie
                    animationData={animationData}
                    loop={true}
                    className={`w-[175px] h-[175px] loading-dots absolute left-0`}
                  />
                </div>
                <div className="ai-logo">
                  <div
                    className="ai-circle praxis-call-ai-circle-bg praxis-call-ai-circle-glow"
                    style={
                      voiceIntensity
                        ? {
                            boxShadow: `0px 0px 15.216px 9.47px color-mix(in srgb, var(--praxis-call-accent, var(--shadow-5, rgb(173, 93, 57))) 27%, transparent) inset, 0 0 0 ${voiceIntensity * 1000 < 20 ? "0" : voiceIntensity * 1000 > 50 ? 50 : voiceIntensity * 1000}px color-mix(in srgb, var(--praxis-call-accent, var(--shadow-5, rgb(173, 93, 57))) ${Math.min(27, ((voiceIntensity * 1000) / 50 > 0.75 ? 0.75 : (voiceIntensity * 1000) / 50) * 100)}%, transparent)`,
                          }
                        : {
                            boxShadow: "0px 0px 15.216px 9.47px color-mix(in srgb, var(--praxis-call-accent, var(--shadow-5, rgb(173, 93, 57))) 27%, transparent) inset",
                          }
                    }
                  >
                    <div className="ai-logo ">
                      {styles?.logo ? <SignedUrlImage s3Key={styles?.smallLogo} bucket={tenantProfile?.assets_bucket} alt="logo" className="w-20 h-20" /> : <Logo />}
                    </div>
                  </div>
                </div>
              </div>
              {isCountdownComplete && (
                <ClockTimer
                  time={callDetails?.beginTime}
                  onTimeChange={(time) => {
                    timeLeftRef.current = time;
                  }}
                  isAbsolute={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sales aid panel: fixed right half, rendered by DocumentViewer when a document is selected */}

      {!isConnected && <PraxisLoading title="PraxisPlay" countdownFrom={0} showCountdown={false} />}
      {showCountdown && <PraxisLoading
          title="PraxisPlay"
          countdownFrom={3}
          onCountdownComplete={() => {
            currentStream.current?.getTracks().forEach((t: MediaStreamTrack) => { t.enabled = true; });
            setShowCountdown(false);
            setIsCountdownComplete(true);
            isCountdownCompleteRef.current = true;
            timeLeftRef.current = Number(callDetails?.beginTime ?? 0) * 60;
          }}
      />}
      {/* PiP Video for Webcam */}
      {callDetails?.isVideoRecording && (
        <video
          ref={webcamVideoRef}
          className="praxis-call-webcam-video"
          muted
          playsInline
        ></video>
      )}
      <TutorialTooltip
        step={currentStep}
        isVisible={shouldShowTutorial}
        onAction={nextStep}
        onSkip={skip}
      />
    </div>
  );
}
